import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Label } from 'recharts';

const obtenerPrimerDiaMes = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().split('T')[0];
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const formatearGuaranies = (valor) => {
    if (valor == null) return 'Gs. 0';
    return 'Gs. ' + Math.round(valor).toLocaleString('es-PY');
};

const formatearAbrev = (valor) => {
    if (valor >= 1000000000) return (valor / 1000000000).toFixed(1) + 'B';
    if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M';
    if (valor >= 1000) return (valor / 1000).toFixed(0) + 'K';
    return Number(valor).toString();
};

// Colores para el grafico circular por tipo de documento
const COLORES_TIPO = {
    'FCO': '#001A70',
    'FCR': '#2DCCD3',
    'FEX': '#C5299B',
    'NCR': '#c62828'
};

const NOMBRES_TIPO = {
    'FCO': 'Factura Contado',
    'FCR': 'Factura Credito',
    'FEX': 'Factura Exportacion',
    'NCR': 'Nota de Credito'
};

function Bienvenida({ usuario, grupo }) {
    const [fechaDesde, setFechaDesde] = useState(obtenerPrimerDiaMes());
    const [fechaHasta, setFechaHasta] = useState(obtenerFechaHoy());
    const [dashboard, setDashboard] = useState(null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => { cargarDashboard(); }, []);

    const cargarDashboard = async () => {
        setCargando(true);
        try {
            const r = await axios.get(getApiUrl('/api/ventas/dashboard'), { params: { fechaDesde, fechaHasta } });
            setDashboard(r.data);
        } catch (e) {
            console.error('Error al cargar dashboard:', e);
        } finally { setCargando(false); }
    };

    // Datos del grafico circular (ventas - NCR)
    const ventasPorTipo = dashboard?.ventasPorTipo || [];
    const totalNeto = ventasPorTipo.reduce((s, v) => s + (v.tipoDoc !== 'NCR' ? Number(v.total) : -Number(v.total)), 0);
    const pieData = ventasPorTipo
        .filter(v => v.tipoDoc !== 'NCR' && Number(v.total) > 0)
        .map(v => ({ name: NOMBRES_TIPO[v.tipoDoc] || v.tipoDoc, codigo: v.tipoDoc, value: Number(v.total) }));
    const montoNCR = ventasPorTipo.find(v => v.tipoDoc === 'NCR')?.total || 0;

    // Datos del grafico de ventas por forma de pago
    const ventasPorFpago = dashboard?.ventasPorFpago || [];
    const COLORES_FPAGO = ['#001A70', '#2DCCD3', '#C5299B', '#AF0F7D', '#00005A', '#7f8c8d', '#16a085', '#e67e22', '#8e44ad', '#27ae60'];
    const pieDataFpago = ventasPorFpago
        .filter(v => Number(v.total) > 0)
        .map((v, idx) => ({
            name: v.idFpago && v.idFpago.length > 18 ? v.idFpago.substring(0, 18) + '...' : (v.idFpago || 'SIN ESPECIFICAR'),
            nomCompleto: v.idFpago || 'SIN ESPECIFICAR',
            value: Number(v.total),
            color: COLORES_FPAGO[idx % COLORES_FPAGO.length]
        }));

    // Top clientes
    const topClientes = dashboard?.topClientes || [];
    const dataClientes = topClientes.slice(0, 5).map(c => ({
        name: c.nomCliente ? (c.nomCliente.length > 20 ? c.nomCliente.substring(0, 20) + '...' : c.nomCliente) : 'Sin nombre',
        nomCompleto: c.nomCliente || 'Sin nombre',
        total: Number(c.totalFacturado) || 0
    }));

    // Top articulos
    const topArticulos = dashboard?.topArticulos || [];
    const dataArticulos = topArticulos.slice(0, 5).map(a => ({
        name: a.descripcion ? (a.descripcion.length > 20 ? a.descripcion.substring(0, 20) + '...' : a.descripcion) : 'Sin desc',
        nomCompleto: a.descripcion || 'Sin desc',
        total: Number(a.totalFacturado) || 0
    }));

    return (
        <div>
            <div className="welcome-card" style={{ marginBottom: '20px' }}>
                <h2 className="welcome-title">¡Bienvenido!</h2>
                <p className="welcome-text">
                    Hola <strong>{usuario}</strong>, es un placer tenerte de vuelta.
                </p>
                <div className="info-box">
                    <div className="info-item">
                        <span className="info-label">Usuario:</span>
                        <span className="info-value">{usuario}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Grupo:</span>
                        <span className="info-value">{grupo}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Estado:</span>
                        <span className="info-value active">Activo</span>
                    </div>
                </div>
            </div>

            {/* Filtro de fechas */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '15px', padding: '15px',
                backgroundColor: 'white', borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px', flexWrap: 'wrap'
            }}>
                <h3 style={{ margin: 0, color: '#001A70' }}>Indicadores de Ventas</h3>
                <label style={{ fontSize: '14px', color: '#555', fontWeight: 500, marginLeft: '20px' }}>Desde:</label>
                <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }} />
                <label style={{ fontSize: '14px', color: '#555', fontWeight: 500 }}>Hasta:</label>
                <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }} />
                <button onClick={cargarDashboard} disabled={cargando}
                    style={{
                        background: 'linear-gradient(135deg, #001A70 0%, #00005A 100%)',
                        color: 'white', border: 'none', padding: '9px 20px', borderRadius: '5px',
                        fontSize: '14px', fontWeight: 600, cursor: cargando ? 'not-allowed' : 'pointer', opacity: cargando ? 0.6 : 1
                    }}>
                    {cargando ? 'Cargando...' : 'Consultar'}
                </button>
            </div>

            {cargando && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d', fontSize: '16px' }}>Cargando datos...</div>
            )}

            {!cargando && dashboard && (
                <>
                    {/* KPI Total */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '20px', marginBottom: '20px'
                    }}>
                        <div style={{
                            background: 'white', padding: '25px', borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center',
                            borderTop: '4px solid #2DCCD3'
                        }}>
                            <div style={{ fontSize: '13px', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                Total Ventas Netas (Ventas - NCR)
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#001A70' }}>
                                {formatearGuaranies(totalNeto)}
                            </div>
                            {montoNCR > 0 && (
                                <div style={{ fontSize: '12px', color: '#c62828', marginTop: '8px' }}>
                                    NCR (Devoluciones): {formatearGuaranies(montoNCR)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Graficos circulares en 2 columnas */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        {/* Grafico circular - Tipo de Documento */}
                        <div style={{
                            background: 'white', padding: '20px', borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ color: '#001A70', marginBottom: '15px', borderBottom: '2px solid #EDF1FB', paddingBottom: '10px' }}>
                                Ventas por Tipo de Documento
                            </h3>
                            {pieData.length > 0 ? (
                                <>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie data={pieData} cx="50%" cy="50%" labelLine={true}
                                                outerRadius={100} fill="#001A70" dataKey="value"
                                                label={({value, percent}) => `Gs. ${formatearAbrev(value)} (${(percent*100).toFixed(1)}%)`}>
                                                {pieData.map((entry, idx) => (
                                                    <Cell key={idx} fill={COLORES_TIPO[entry.codigo] || '#7f8c8d'} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatearGuaranies(value)} />
                                            <Legend wrapperStyle={{ fontSize: '12px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div style={{
                                        marginTop: '10px', padding: '12px', backgroundColor: '#EDF1FB', borderRadius: '6px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>Total</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#001A70' }}>
                                            {formatearGuaranies(pieData.reduce((s, d) => s + d.value, 0))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    Sin datos en el periodo seleccionado
                                </div>
                            )}
                        </div>

                        {/* Grafico circular - Forma de Pago */}
                        <div style={{
                            background: 'white', padding: '20px', borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ color: '#001A70', marginBottom: '15px', borderBottom: '2px solid #EDF1FB', paddingBottom: '10px' }}>
                                Ventas por Forma de Pago
                            </h3>
                            {pieDataFpago.length > 0 ? (
                                <>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie data={pieDataFpago} cx="50%" cy="50%" labelLine={true}
                                                outerRadius={100} fill="#001A70" dataKey="value"
                                                label={({value, percent}) => `Gs. ${formatearAbrev(value)} (${(percent*100).toFixed(1)}%)`}>
                                                {pieDataFpago.map((entry, idx) => (
                                                    <Cell key={idx} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatearGuaranies(value)}
                                                labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.nomCompleto : label} />
                                            <Legend wrapperStyle={{ fontSize: '12px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div style={{
                                        marginTop: '10px', padding: '12px', backgroundColor: '#EDF1FB', borderRadius: '6px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>Total</div>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#001A70' }}>
                                            {formatearGuaranies(pieDataFpago.reduce((s, d) => s + d.value, 0))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    Sin datos en el periodo seleccionado
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top 5 Clientes y Top 5 Articulos en 2 columnas */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        {/* Top 5 Clientes */}
                        <div style={{
                            background: 'white', padding: '20px', borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ color: '#001A70', marginBottom: '15px', borderBottom: '2px solid #EDF1FB', paddingBottom: '10px' }}>
                                Top 5 Clientes
                            </h3>
                            {dataClientes.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={dataClientes} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                        <XAxis type="number" tickFormatter={formatearAbrev} tick={{ fontSize: 11, fill: '#7f8c8d' }} />
                                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: '#333' }} />
                                        <Tooltip formatter={(value) => [formatearGuaranies(value), 'Total']}
                                            labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.nomCompleto : label} />
                                        <Bar dataKey="total" fill="#2DCCD3" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    Sin datos
                                </div>
                            )}
                        </div>

                        {/* Top 5 Articulos */}
                        <div style={{
                            background: 'white', padding: '20px', borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ color: '#001A70', marginBottom: '15px', borderBottom: '2px solid #EDF1FB', paddingBottom: '10px' }}>
                                Top 5 Articulos
                            </h3>
                            {dataArticulos.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={dataArticulos} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                        <XAxis type="number" tickFormatter={formatearAbrev} tick={{ fontSize: 11, fill: '#7f8c8d' }} />
                                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: '#333' }} />
                                        <Tooltip formatter={(value) => [formatearGuaranies(value), 'Total']}
                                            labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.nomCompleto : label} />
                                        <Bar dataKey="total" fill="#001A70" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d', fontStyle: 'italic' }}>
                                    Sin datos
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Bienvenida;
